/**
 * Internationalization (i18n) Service
 *
 * Provides translation lookup, language detection, and runtime language switching.
 * Uses static JSON translation bundles bundled at build time.
 */

import en from '../i18n/en.json';

// Import other languages - will be created
// Lazy load to reduce initial bundle
type TranslationBundle = Record<string, unknown>;
const languageBundles: Record<string, TranslationBundle | (() => Promise<TranslationBundle>)> = {
  en: en as TranslationBundle,
  // Western European
  es: () => import('../i18n/es.json').then(m => m.default as TranslationBundle),
  fr: () => import('../i18n/fr.json').then(m => m.default as TranslationBundle),
  de: () => import('../i18n/de.json').then(m => m.default as TranslationBundle),
  pt: () => import('../i18n/pt.json').then(m => m.default as TranslationBundle),
  it: () => import('../i18n/it.json').then(m => m.default as TranslationBundle),
  nl: () => import('../i18n/nl.json').then(m => m.default as TranslationBundle),
  ca: () => import('../i18n/ca.json').then(m => m.default as TranslationBundle),
  // Nordic
  sv: () => import('../i18n/sv.json').then(m => m.default as TranslationBundle),
  no: () => import('../i18n/no.json').then(m => m.default as TranslationBundle),
  da: () => import('../i18n/da.json').then(m => m.default as TranslationBundle),
  fi: () => import('../i18n/fi.json').then(m => m.default as TranslationBundle),
  // Central/Eastern European
  pl: () => import('../i18n/pl.json').then(m => m.default as TranslationBundle),
  cs: () => import('../i18n/cs.json').then(m => m.default as TranslationBundle),
  sk: () => import('../i18n/sk.json').then(m => m.default as TranslationBundle),
  hu: () => import('../i18n/hu.json').then(m => m.default as TranslationBundle),
  ro: () => import('../i18n/ro.json').then(m => m.default as TranslationBundle),
  sl: () => import('../i18n/sl.json').then(m => m.default as TranslationBundle),
  hr: () => import('../i18n/hr.json').then(m => m.default as TranslationBundle),
  sr: () => import('../i18n/sr.json').then(m => m.default as TranslationBundle),
  bg: () => import('../i18n/bg.json').then(m => m.default as TranslationBundle),
  // Baltic
  lt: () => import('../i18n/lt.json').then(m => m.default as TranslationBundle),
  lv: () => import('../i18n/lv.json').then(m => m.default as TranslationBundle),
  et: () => import('../i18n/et.json').then(m => m.default as TranslationBundle),
  // Other European
  el: () => import('../i18n/el.json').then(m => m.default as TranslationBundle),
  uk: () => import('../i18n/uk.json').then(m => m.default as TranslationBundle),
  ru: () => import('../i18n/ru.json').then(m => m.default as TranslationBundle),
  tr: () => import('../i18n/tr.json').then(m => m.default as TranslationBundle),
  // Asian
  ja: () => import('../i18n/ja.json').then(m => m.default as TranslationBundle),
  zh: () => import('../i18n/zh.json').then(m => m.default as TranslationBundle),
  ko: () => import('../i18n/ko.json').then(m => m.default as TranslationBundle),
  // South/Southeast Asian
  hi: () => import('../i18n/hi.json').then(m => m.default as TranslationBundle),
  th: () => import('../i18n/th.json').then(m => m.default as TranslationBundle),
  vi: () => import('../i18n/vi.json').then(m => m.default as TranslationBundle),
  // Celtic
  cy: () => import('../i18n/cy.json').then(m => m.default as TranslationBundle),
  ga: () => import('../i18n/ga.json').then(m => m.default as TranslationBundle),
  // Middle Eastern
  ar: () => import('../i18n/ar.json').then(m => m.default as TranslationBundle),
  fa: () => import('../i18n/fa.json').then(m => m.default as TranslationBundle),
  he: () => import('../i18n/he.json').then(m => m.default as TranslationBundle),
  ur: () => import('../i18n/ur.json').then(m => m.default as TranslationBundle),
  // Caucasus/Turkic
  az: () => import('../i18n/az.json').then(m => m.default as TranslationBundle),
  hy: () => import('../i18n/hy.json').then(m => m.default as TranslationBundle),
  ka: () => import('../i18n/ka.json').then(m => m.default as TranslationBundle),
};

/**
 * Supported language codes
 */
export type SupportedLanguage =
  | 'en' | 'es' | 'fr' | 'de' | 'pt' | 'it' | 'nl' | 'ca'
  | 'sv' | 'no' | 'da' | 'fi'
  | 'pl' | 'cs' | 'sk' | 'hu' | 'ro' | 'sl' | 'hr' | 'sr' | 'bg'
  | 'lt' | 'lv' | 'et'
  | 'el' | 'uk' | 'ru' | 'tr'
  | 'hi' | 'th' | 'vi'
  | 'cy' | 'ga'
  | 'ja' | 'zh' | 'ko'
  | 'ar' | 'fa' | 'he' | 'ur'
  | 'az' | 'hy' | 'ka';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  'en', 'es', 'fr', 'de', 'pt', 'it', 'nl', 'ca',
  'sv', 'no', 'da', 'fi',
  'pl', 'cs', 'sk', 'hu', 'ro', 'sl', 'hr', 'sr', 'bg',
  'lt', 'lv', 'et',
  'el', 'uk', 'ru', 'tr',
  'hi', 'th', 'vi',
  'cy', 'ga',
  'ja', 'zh', 'ko',
  'ar', 'fa', 'he', 'ur',
  'az', 'hy', 'ka',
];

/**
 * I18n configuration
 */
export interface I18nConfig {
  /** Override browser language detection */
  language?: string;
  /** Fallback language if translation missing (default: 'en') */
  fallbackLanguage?: string;
}

/**
 * I18n Service - singleton for managing translations
 */
class I18nService {
  private currentLanguage: SupportedLanguage = 'en';
  private fallbackLanguage: SupportedLanguage = 'en';
  private translations: TranslationBundle = en as TranslationBundle;
  private fallbackTranslations: TranslationBundle = en as TranslationBundle;
  private loadingLanguage: Promise<void> | null = null;

  /**
   * Initialize i18n with configuration
   */
  async init(config: I18nConfig = {}): Promise<void> {
    // Set fallback
    this.fallbackLanguage = this.normalizeLanguage(config.fallbackLanguage) || 'en';

    // Determine language: config override > browser detection > fallback
    const targetLanguage = config.language
      ? this.normalizeLanguage(config.language)
      : this.detectBrowserLanguage();

    if (targetLanguage && targetLanguage !== 'en') {
      await this.setLanguage(targetLanguage);
    }
  }

  /**
   * Get current language code
   */
  getLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  /**
   * Set active language
   */
  async setLanguage(lang: SupportedLanguage): Promise<void> {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      console.warn(`[i18n] Language '${lang}' not supported, using '${this.fallbackLanguage}'`);
      lang = this.fallbackLanguage;
    }

    if (lang === this.currentLanguage) return;

    // Load translations
    const bundle = languageBundles[lang];
    if (!bundle) {
      console.warn(`[i18n] No bundle found for language '${lang}'`);
      return;
    }
    if (typeof bundle === 'function') {
      this.loadingLanguage = bundle().then(loaded => {
        this.translations = loaded;
        this.currentLanguage = lang;
        this.loadingLanguage = null;
        updateRtlDirection();
      }).catch(err => {
        console.error(`[i18n] Failed to load language '${lang}':`, err);
        this.loadingLanguage = null;
      });
      await this.loadingLanguage;
    } else {
      this.translations = bundle;
      this.currentLanguage = lang;
      updateRtlDirection();
    }
  }

  /**
   * Translate a key path (e.g., 'panel.title')
   */
  t(keyPath: string, params?: Record<string, string | number>): string {
    let result = this.getNestedValue(this.translations, keyPath);

    // Fallback to default language if not found
    if (result === undefined) {
      result = this.getNestedValue(this.fallbackTranslations, keyPath);
    }

    // Return key if still not found
    if (result === undefined) {
      console.warn(`[i18n] Missing translation for '${keyPath}'`);
      return keyPath;
    }

    // Parameter substitution: {{name}} -> value
    if (params && typeof result === 'string') {
      result = result.replace(/\{\{(\w+)\}\}/g, (_, key) => {
        return params[key] !== undefined ? String(params[key]) : `{{${key}}}`;
      });
    }

    return String(result);
  }

  /**
   * Check if a translation exists
   */
  has(keyPath: string): boolean {
    return this.getNestedValue(this.translations, keyPath) !== undefined ||
           this.getNestedValue(this.fallbackTranslations, keyPath) !== undefined;
  }

  /**
   * Detect browser language
   */
  private detectBrowserLanguage(): SupportedLanguage | null {
    const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || '';
    return this.normalizeLanguage(browserLang);
  }

  /**
   * Normalize language code to supported language
   */
  private normalizeLanguage(lang: string | undefined): SupportedLanguage | null {
    if (!lang) return null;

    // Extract base language (e.g., 'en-US' -> 'en')
    const base = lang.toLowerCase().split('-')[0];

    if (SUPPORTED_LANGUAGES.includes(base as SupportedLanguage)) {
      return base as SupportedLanguage;
    }

    return null;
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: TranslationBundle, path: string): unknown {
    return path.split('.').reduce((acc: unknown, part: string) => {
      if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj);
  }
}

// =============================================================================
// RTL SUPPORT
// =============================================================================

const RTL_LANGUAGES: ReadonlySet<string> = new Set(['ar', 'fa', 'he', 'ur']);

/** Element to receive dir="rtl" when an RTL language is active. */
let rtlContainer: HTMLElement | null = null;

/**
 * Check if a language uses right-to-left script.
 */
export function isRtlLanguage(lang: string): boolean {
  return RTL_LANGUAGES.has(lang);
}

/**
 * Register the widget panel element for automatic RTL direction.
 * Call this once from panel.ts after creating the panel element.
 */
export function setRtlContainer(el: HTMLElement): void {
  rtlContainer = el;
  // Apply current direction immediately
  updateRtlDirection();
}

function updateRtlDirection(): void {
  if (!rtlContainer) return;
  if (isRtlLanguage(i18nService.getLanguage())) {
    rtlContainer.setAttribute('dir', 'rtl');
  } else {
    rtlContainer.removeAttribute('dir');
  }
}

// Singleton instance
const i18nService = new I18nService();

/**
 * Initialize i18n - call once at widget startup
 */
export async function initI18n(config: I18nConfig = {}): Promise<void> {
  await i18nService.init(config);
}

/**
 * Get current language
 */
export function getLanguage(): SupportedLanguage {
  return i18nService.getLanguage();
}

/**
 * Set active language
 */
export async function setLanguage(lang: SupportedLanguage): Promise<void> {
  await i18nService.setLanguage(lang);
}

/**
 * Translate a key - primary function for translations
 *
 * @example
 * t('panel.title') // "Ask me anything"
 * t('messages.error', { code: 500 }) // "Error: 500"
 */
export function t(keyPath: string, params?: Record<string, string | number>): string {
  return i18nService.t(keyPath, params);
}

/**
 * Check if translation exists
 */
export function hasTranslation(keyPath: string): boolean {
  return i18nService.has(keyPath);
}

/**
 * Detect language from text input using character analysis.
 * Returns detected language code or null if uncertain.
 *
 * Detects all supported European languages plus Asian/Middle Eastern scripts.
 * The backend uses this to instruct Claude to respond in the user's language.
 */
export function detectLanguageFromText(text: string): string | null {
  if (!text || text.length < 10) return null;

  // Japanese: Contains Hiragana, Katakana, or significant Kanji
  if (/[\u3040-\u309F\u30A0-\u30FF]/.test(text)) {
    return 'ja';
  }

  // Korean: Contains Hangul
  if (/[\uAC00-\uD7AF\u1100-\u11FF]/.test(text)) {
    return 'ko';
  }

  // Chinese: Contains CJK unified ideographs without Japanese kana
  if (/[\u4E00-\u9FFF]/.test(text) && !/[\u3040-\u309F\u30A0-\u30FF]/.test(text)) {
    return 'zh';
  }

  // Greek: Contains Greek script
  if (/[\u0370-\u03FF]/.test(text)) {
    return 'el';
  }

  // Ukrainian: Cyrillic with unique Ukrainian letters (і, ї, є, ґ)
  if (/[\u0400-\u04FF]/.test(text) && /[іїєґ]/i.test(text)) {
    return 'uk';
  }

  // Arabic: Contains Arabic script
  if (/[\u0600-\u06FF]/.test(text)) {
    return 'ar';
  }

  // Common word patterns for language detection
  const patterns: Record<string, RegExp[]> = {
    // Nordic
    da: [
      /\b(hej|tak|venligst|hvordan|hvad|hvor|hvorn.r|hvorfor|denne|dette|den|det|kan|vil|skal|ikke|meget|godt|har|er|med|til|fra|eller|men|ogs.)\b/i,
      /[æøå]/i,
    ],
    no: [
      /\b(hei|takk|vennligst|hvordan|hva|hvor|når|hvorfor|denne|dette|den|det|kan|vil|skal|ikke|mye|bra|har|er|med|til|fra|eller|men|også)\b/i,
      /[æøå]/i,
    ],
    sv: [
      /\b(hej|tack|snälla|hur|vad|var|när|varför|denna|detta|den|det|kan|vill|ska|inte|mycket|bra|har|är|med|till|från|eller|men|också)\b/i,
      /[åäö]/i,
    ],
    fi: [
      /\b(hei|kiitos|ole hyvä|miten|mikä|missä|milloin|miksi|tämä|tuo|voi|haluan|tarvitsen|ei|paljon|hyvä|nyt|myös|mutta|ovat|olen)\b/i,
      /[äö]/i,
      /\b(ja|tai|kanssa|että|kun|jos|niin|ovat|olla|minä|sinä)\b/i,
    ],
    // Western European
    nl: [
      /\b(hallo|dank|alstublieft|hoe|wat|waar|wanneer|waarom|deze|dit|die|dat|kan|wil|zal|niet|veel|goed|heeft|ben|met|naar|van|of|maar|ook)\b/i,
    ],
    pt: [
      /\b(olá|obrigado|por favor|como|o que|onde|quando|por que|este|esta|isso|pode|quer|precisa|não|muito|bem|agora|também|mas)\b/i,
      /[ãõçâêô]/,
    ],
    it: [
      /\b(ciao|grazie|per favore|come|cosa|dove|quando|perché|questo|questa|quello|può|vuole|non|molto|bene|adesso|anche|ma|sono)\b/i,
      /[àèéìòù]/,
    ],
    ca: [
      /\b(hola|gràcies|si us plau|com|què|on|quan|per què|aquest|aquesta|això|pot|vol|no|molt|bé|ara|també|però|són)\b/i,
      /[àèéíïòóúüç·]/,
    ],
    es: [
      /\b(hola|gracias|por favor|cómo|qué|está|están|tengo|quiero|puedo|necesito|dónde|cuándo|porque|pero|también|muy|bien|mal|ahora)\b/i,
      /[áéíóúñ¿¡]/,
    ],
    fr: [
      /\b(bonjour|merci|s'il vous plaît|comment|qu'est|je suis|j'ai|je veux|je peux|où|quand|pourquoi|mais|aussi|très|bien|maintenant)\b/i,
      /[àâäéèêëïîôùûüÿœæç]/,
    ],
    de: [
      /\b(hallo|danke|bitte|wie|was|ich bin|ich habe|ich will|ich kann|wo|wann|warum|aber|auch|sehr|gut|jetzt|nicht|bieten|brauche|möchte|gibt|welche|diese|einen|keine|mein|mehr|noch|alle)\b/i,
      /[äöüß]/,
      /\b(und|oder|für|mit|auf|aus|bei|nach|von|zu|Sie|Ihnen|uns|wir|sind|haben|können|über|ihre|unser)\b/i,
    ],
    // Central/Eastern European
    pl: [
      /\b(cześć|dzięki|proszę|jak|co|gdzie|kiedy|dlaczego|ten|ta|to|mogę|chcę|nie|bardzo|dobrze|teraz|też|ale|jest)\b/i,
      /[ąćęłńóśźż]/i,
    ],
    cs: [
      /\b(ahoj|díky|prosím|jak|co|kde|kdy|proč|tento|tato|toto|mohu|chci|ne|velmi|dobře|teď|také|ale|jest)\b/i,
      /[áčďéěíňóřšťúůýž]/i,
    ],
    sk: [
      /\b(ahoj|ďakujem|prosím|ako|čo|kde|kedy|prečo|tento|táto|toto|môžem|chcem|nie|veľmi|dobre|teraz|tiež|ale|jest)\b/i,
      /[áäčďéíľĺňóôŕšťúýž]/i,
    ],
    hu: [
      /\b(szia|köszönöm|kérem|hogyan|mi|hol|mikor|miért|ez|az|tudok|akarok|nem|nagyon|jó|most|is|de|van|vagy)\b/i,
      /[áéíóöőúüű]/i,
    ],
    ro: [
      /\b(bună|mulțumesc|vă rog|cum|ce|unde|când|de ce|acest|această|pot|vreau|nu|foarte|bine|acum|și|dar|este|sunt)\b/i,
      /[ăâîșț]/i,
    ],
    sl: [
      /\b(zdravo|hvala|prosim|kako|kaj|kje|kdaj|zakaj|ta|to|lahko|želim|ne|zelo|dobro|zdaj|tudi|ampak|je|sem)\b/i,
      /[čšž]/i,
    ],
    hr: [
      /\b(bok|hvala|molim|kako|što|gdje|kada|zašto|ovaj|ova|ovo|mogu|želim|ne|vrlo|dobro|sada|također|ali|jest)\b/i,
      /[čćđšž]/i,
    ],
    sr: [
      /\b(zdravo|hvala|molim|kako|šta|gde|kada|zašto|ovaj|ova|ovo|mogu|želim|ne|vrlo|dobro|sada|takođe|ali|jest)\b/i,
      /[čćđšž]/i,
    ],
    bg: [
      /[\u0400-\u04FF]/,
      /\b(здравей|благодаря|моля|как|какво|къде|кога|защо|мога|искам|не|много|добре|сега|също|но)\b/i,
    ],
    // Baltic
    lt: [
      /\b(labas|ačiū|prašau|kaip|kas|kur|kada|kodėl|šis|ši|galiu|noriu|ne|labai|gerai|dabar|taip pat|bet|yra)\b/i,
      /[ąčęėįšųūž]/i,
    ],
    lv: [
      /\b(sveiki|paldies|lūdzu|kā|kas|kur|kad|kāpēc|šis|šī|varu|gribu|nē|ļoti|labi|tagad|arī|bet|ir)\b/i,
      /[āčēģīķļņšūž]/i,
    ],
    et: [
      /\b(tere|tänan|palun|kuidas|mis|kus|millal|miks|see|saan|tahan|ei|väga|hästi|nüüd|ka|aga|on|olen)\b/i,
      /[äöüõšž]/i,
    ],
    // Other European
    el: [
      /[\u0370-\u03FF]/,
      /\b(γεια|ευχαριστώ|παρακαλώ|πώς|τι|πού|πότε|γιατί|μπορώ|θέλω|δεν|πολύ|καλά|τώρα|επίσης|αλλά)\b/i,
    ],
    uk: [
      /[\u0400-\u04FF]/,
      /\b(привіт|дякую|будь ласка|як|що|де|коли|чому|можу|хочу|не|дуже|добре|зараз|також|але)\b/i,
      /[іїєґ]/i,
    ],
    ru: [
      /[\u0400-\u04FF]/,
      /\b(привет|спасибо|пожалуйста|как|что|где|когда|почему|могу|хочу|не|очень|хорошо|сейчас|тоже|но)\b/i,
    ],
    tr: [
      /\b(merhaba|teşekkür|lütfen|nasıl|ne|nerede|ne zaman|neden|bu|şu|yapabilir|istiyorum|değil|çok|iyi|şimdi|da|ama|var)\b/i,
      /[çğıöşü]/i,
    ],
    en: [
      /\b(hello|hi|thanks|please|how|what|where|when|why|the|and|but|also|very|good|bad|now|want|need|can|could|would|should|do|does|did)\b/i,
      /\b(your|my|our|their|who|whos|whose|which|that|this|these|those|about|from|with|for|into|have|has|had|are|is|was|were|been|will|just|get|got|some|any|more|them|they|you|its|here|there|offer|services|available)\b/i,
      /\b(tell|show|give|find|know|make|take|look|help|think|see|like|new|team|much|many|other|each|every|only|still|than|then|because|really|actually|sure|great|right|best|most|own|pricing|products|book|appointment|contact|information)\b/i,
    ],
  };

  // Score each language
  const scores: Record<string, number> = {};
  for (const lang of Object.keys(patterns)) {
    scores[lang] = 0;
  }

  for (const [lang, regexes] of Object.entries(patterns)) {
    for (const regex of regexes) {
      const matches = text.match(new RegExp(regex, 'gi'));
      if (matches) {
        scores[lang] = (scores[lang] ?? 0) + matches.length;
      }
    }
  }

  // Find highest scoring language
  let maxScore = 0;
  let detectedLang: string | null = null;

  for (const [lang, score] of Object.entries(scores)) {
    if (score > maxScore && score >= 2) { // Require at least 2 matches
      maxScore = score;
      detectedLang = lang;
    }
  }

  return detectedLang;
}

/**
 * Auto-detect and switch language based on user input.
 * Call this after the first user message.
 * Returns true if language was switched.
 */
export async function autoDetectAndSwitch(text: string): Promise<boolean> {
  const detected = detectLanguageFromText(text);

  // Only switch widget UI language if the detected language has UI translations
  if (detected && SUPPORTED_LANGUAGES.includes(detected as SupportedLanguage)
      && detected !== i18nService.getLanguage()) {
    await i18nService.setLanguage(detected as SupportedLanguage);
    return true;
  }

  return false;
}
